import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import ReportIssueDialog from './ReportIssueDialog';

/**
 * Persistent floating action button (mounted once at the app-shell level) that
 * lets any user capture the current viewport, annotate it, and report an issue
 * straight to the support team.
 */
export default function ReportIssueButton() {
    const toast = useRef(null);
    const buttonWrapperRef = useRef(null);
    const [capturing, setCapturing] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [screenshot, setScreenshot] = useState(null);

    const captureAndOpen = async () => {
        setCapturing(true);
        try {
            // Wait a frame so the button's hidden state actually paints before capture.
            await new Promise((resolve) => requestAnimationFrame(resolve));
            // Capture the full scrollable page (not just the current viewport slice) so
            // the reporter can annotate content that's below the fold too — the dialog
            // scrolls vertically to show all of it.
            const fullWidth = document.documentElement.scrollWidth;
            const fullHeight = document.documentElement.scrollHeight;
            const canvas = await html2canvas(document.body, {
                width: fullWidth,
                height: fullHeight,
                windowWidth: fullWidth,
                windowHeight: fullHeight,
                useCORS: true,
                // html2canvas can't tell that browsers hide <noscript> when JS is enabled
                // (that's UA behavior, not a readable CSS rule), so it sometimes renders its
                // text on top of the page — exclude it explicitly.
                ignoreElements: (el) => el.classList?.contains('report-issue-ignore') || el.tagName === 'NOSCRIPT'
            });
            setScreenshot(canvas.toDataURL('image/png'));
            setDialogVisible(true);
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            toast.current?.show({ severity: 'error', summary: 'Capture Failed', detail: 'Could not capture a screenshot of this page.' });
        } finally {
            setCapturing(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <div ref={buttonWrapperRef} className="report-issue-ignore" style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 9999, display: capturing ? 'none' : 'block' }}>
                <Button
                    icon="pi pi-flag"
                    rounded
                    severity="danger"
                    tooltip="Report an Issue"
                    tooltipOptions={{ position: 'left' }}
                    onClick={captureAndOpen}
                    loading={capturing}
                    style={{ width: 52, height: 52, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}
                />
            </div>

            {dialogVisible && (
                <ReportIssueDialog
                    visible={dialogVisible}
                    onHide={() => {
                        setDialogVisible(false);
                        setScreenshot(null);
                    }}
                    imageSrc={screenshot}
                    toast={toast}
                />
            )}
        </>
    );
}
